class HeroChroxelSlider extends HTMLElement {
  constructor() {
    super();
    this.current = 0;
    this.timer = null;
    this.pointerStartX = 0;
    this.pointerDeltaX = 0;
    this.isDragging = false;
    this.activePointerId = null;
  }

  connectedCallback() {
    this.track = this.querySelector('.hero-chroxel__track');
    this.slides = Array.from(this.querySelectorAll('.hero-chroxel__slide:not([data-clone])'));
    this.dots = Array.from(this.querySelectorAll('.hero-chroxel__dot'));
    this.prevBtn = this.querySelector('.hero-chroxel__arrow--prev');
    this.nextBtn = this.querySelector('.hero-chroxel__arrow--next');

    this.total = this.slides.length;
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    this.wrap = true;

    if (this.total <= 0) return;

    this.setupClones();
    this.bindEvents();
    this.track.style.transition = 'none';
    this.goTo(0);
    this.track.offsetHeight;
    this.track.style.transition = '';
    this.onResize = this.recalculatePosition.bind(this);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.stop();
    this.unbindEvents();
    window.removeEventListener('resize', this.onResize);
  }

  setupClones() {
    this.track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
    this.cloneOffset = 0;
    if (this.total <= 1) return;

    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-slide-active');
      clone.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.muted = true;
      });
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      return clone;
    };

    this.track.prepend(makeClone(this.slides[this.total - 1]));
    this.track.append(makeClone(this.slides[0]));
    this.cloneOffset = 1;
  }

  getGap() {
    return parseInt(getComputedStyle(this.track).gap) || 0;
  }

  getSlideWidth() {
    return this.slides[0] ? this.slides[0].offsetWidth : 0;
  }

  getViewportWidth() {
    return this.track.parentElement ? this.track.parentElement.offsetWidth : 0;
  }

  getCenteredOffset() {
    const slideWidth = this.getSlideWidth();
    const gap = this.getGap();
    const viewportWidth = this.getViewportWidth();
    const peek = (viewportWidth - slideWidth) / 2;
    return -(this.position * (slideWidth + gap)) + peek;
  }

  recalculatePosition() {
    this.update();
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideTarget, 10);
        this.goTo(idx);
        this.restart();
      });
    });

    this.track.addEventListener('transitionend', this.onTransitionEnd.bind(this));

    this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.track.addEventListener('pointerup', this.onPointerEnd.bind(this));
    this.track.addEventListener('pointercancel', this.onPointerEnd.bind(this));
    this.track.addEventListener('pointerleave', this.onPointerEnd.bind(this));

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => {
      if (this.shouldAutoplay()) this.start();
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));
  }

  unbindEvents() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange.bind(this));
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.stop();
    } else if (this.shouldAutoplay()) {
      this.start();
    }
  }

  onPointerDown(e) {
    if (e.target.closest('.hero-chroxel__arrow, .hero-chroxel__dot, .hero-chroxel__button, a, button')) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    this.pointerStartX = e.clientX;
    this.pointerDeltaX = 0;
    this.isDragging = true;
    this.activePointerId = e.pointerId;
    this.settle();
    this.track.style.transition = 'none';

    try {
      this.track.setPointerCapture(e.pointerId);
    } catch (err) {
      // Some browsers throw if pointer capture is not supported
    }

    this.stop();
  }

  onPointerMove(e) {
    if (!this.isDragging || e.pointerId !== this.activePointerId) return;
    if (e.cancelable) e.preventDefault();

    this.pointerDeltaX = e.clientX - this.pointerStartX;
    const slideWidth = this.getSlideWidth();
    const gap = this.getGap();
    const viewportWidth = this.getViewportWidth();
    const slideStep = slideWidth + gap;
    const baseX = this.getCenteredOffset();
    const offset = baseX + (this.pointerDeltaX / viewportWidth) * slideStep;
    this.track.style.transform = `translateX(${offset}px)`;
  }

  onPointerEnd(e) {
    if (!this.isDragging) return;
    if (e && e.pointerId !== this.activePointerId) return;

    this.isDragging = false;
    this.activePointerId = null;
    this.track.style.transition = '';

    const slideWidth = this.getSlideWidth();
    const threshold = slideWidth * 0.15;
    if (this.pointerDeltaX < -threshold) {
      this.next();
    } else if (this.pointerDeltaX > threshold) {
      this.prev();
    } else {
      this.update();
    }

    if (this.shouldAutoplay()) this.start();
  }

  next() {
    this.step(1);
    this.restart();
  }

  prev() {
    this.step(-1);
    this.restart();
  }

  step(direction) {
    if (this.total <= 1) return;
    this.settle();
    this.position += direction;
    this.current = (this.current + direction + this.total) % this.total;
    this.update();
    this.manageVideoPlayback();
    this.manageAutoplay();
  }

  goTo(index) {
    if (this.wrap) {
      this.current = (index + this.total) % this.total;
    } else {
      this.current = Math.max(0, Math.min(index, this.total - 1));
    }
    this.position = this.current + (this.cloneOffset || 0);
    this.update();
    this.manageVideoPlayback();
    this.manageAutoplay();
  }

  onTransitionEnd(e) {
    if (e.target !== this.track || e.propertyName !== 'transform') return;
    this.settle();
  }

  settle() {
    if (!this.cloneOffset) return;
    if (this.position === 0) {
      this.position = this.total;
      this.jumpWithoutTransition();
    } else if (this.position === this.total + 1) {
      this.position = 1;
      this.jumpWithoutTransition();
    }
  }

  jumpWithoutTransition() {
    this.track.style.transition = 'none';
    this.track.style.transform = `translateX(${this.getCenteredOffset()}px)`;
    this.track.offsetHeight;
    this.track.style.transition = '';
  }

  update() {
    this.track.style.transform = `translateX(${this.getCenteredOffset()}px)`;

    this.slides.forEach((slide, i) => {
      if (i === this.current) {
        slide.setAttribute('data-slide-active', '');
      } else {
        slide.removeAttribute('data-slide-active');
      }
    });

    this.dots.forEach((dot, i) => {
      if (i === this.current) {
        dot.classList.add('hero-chroxel__dot--active');
        dot.setAttribute('aria-selected', 'true');
      } else {
        dot.classList.remove('hero-chroxel__dot--active');
        dot.setAttribute('aria-selected', 'false');
      }
    });

    if (this.prevBtn) {
      if (this.wrap || this.current > 0) {
        this.prevBtn.removeAttribute('disabled');
      } else {
        this.prevBtn.setAttribute('disabled', '');
      }
    }

    if (this.nextBtn) {
      if (this.wrap || this.current < this.total - 1) {
        this.nextBtn.removeAttribute('disabled');
      } else {
        this.nextBtn.setAttribute('disabled', '');
      }
    }
  }

  hasVideo(slide) {
    return !!slide.querySelector('.hero-chroxel__video');
  }

  manageVideoPlayback() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('.hero-chroxel__video');
      if (!video) return;

      if (i === this.current) {
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        video.removeEventListener('ended', this.onVideoEnded);
        video.addEventListener('ended', this.onVideoEnded, { once: true });
      } else {
        video.pause();
      }
    });
  }

  onVideoEnded() {
    if (this.shouldAutoplay()) {
      this.start();
    }
  }

  shouldAutoplay() {
    if (!this.autoplay) return false;
    const activeSlide = this.slides[this.current];
    if (!activeSlide) return true;
    return !this.hasVideo(activeSlide);
  }

  manageAutoplay() {
    if (this.shouldAutoplay()) {
      this.start();
    } else {
      this.stop();
    }
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.step(1), this.speed);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    if (this.shouldAutoplay()) this.start();
  }
}

customElements.define('hero-chroxel', HeroChroxelSlider);
